import type { PastEmail } from '../../types/interfaces/customer.interface'

/** How many times a person may change their sign-in e-mail in any `EMAIL_CHANGE_WINDOW_DAYS` (docs/account.md). */
export const EMAIL_CHANGES_PER_WINDOW = 2
export const EMAIL_CHANGE_WINDOW_DAYS = 365
/** How many confirmation e-mails one account may ask for in a day - an address must not be spammed through Seiri. */
export const EMAIL_CHANGE_REQUESTS_PER_DAY = 3
/** How long a confirmation link works. */
export const EMAIL_CHANGE_LINK_HOURS = 24
/** Where a person who has used up their changes is sent. The address every Seiri e-mail comes from. */
export const SEIRI_SUPPORT_EMAIL = 'support@seiri.sk'

const DAY_MS = 24 * 60 * 60 * 1000

export interface EmailChangeAllowance {
  /** Changes left in the window, 0 when used up. */
  remaining: number
  /** When the oldest counted change leaves the window and one more is possible; `null` while one is left. */
  nextAllowedAt: Date | null
}

/**
 * How many more times the person may change their sign-in e-mail. Only their own confirmed changes count
 * (`reason: 'selfChange'`) within the last `EMAIL_CHANGE_WINDOW_DAYS`, a rolling window: an address replaced at
 * registration from an invitation, or before the limit existed, does not.
 */
export function emailChangeAllowance(pastEmails: readonly PastEmail[], now: Date = new Date()): EmailChangeAllowance {
  const windowStart = now.getTime() - EMAIL_CHANGE_WINDOW_DAYS * DAY_MS
  const counted = pastEmails
    .filter((entry) => entry.reason === 'selfChange')
    .map((entry) => new Date(entry.replacedAt).getTime())
    .filter((time) => time > windowStart)
    .sort((a, b) => a - b)

  // The change that has to leave the window before another fits; there is none while one is left.
  const blocking = counted.at(-EMAIL_CHANGES_PER_WINDOW)
  if (blocking === undefined) {
    return { remaining: EMAIL_CHANGES_PER_WINDOW - counted.length, nextAllowedAt: null }
  }
  return { remaining: 0, nextAllowedAt: new Date(blocking + EMAIL_CHANGE_WINDOW_DAYS * DAY_MS) }
}
